"use client";

import {
	type ParkerContextValue,
	ParkerStateProvider,
	useParker,
} from "@offstreetparking/portal-flow/providers/parker";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCompany } from "~/components/providers/CompanyProvider";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { offstreetClient } from "~/services/offstreetApi";

export { useParker };
export type { ParkerContextValue };

export const ParkerProvider = ({ children }: { children: React.ReactNode }) => {
	const { globalParkerFlag, isPending: isFeatureFlagsPending } = useFeatureFlags();
	const { isFromSubdomain } = useCompany();
	const parkerAccountsEnabled = isFromSubdomain || !!globalParkerFlag;
	const utils = offstreetClient.useUtils();

	const auth = useAuth({ ensureSignedIn: false });
	const enabled = !!auth.user?.externalId && !auth.loading && parkerAccountsEnabled;
	const {
		data: parker,
		isPending: isParkerPending,
		error,
		refetch: refetchParker,
	} = offstreetClient.parkers.getParkerSession.useQuery(undefined, {
		enabled,
	});
	const isLoading = (isParkerPending && enabled) || auth.loading || isFeatureFlagsPending;

	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	useEffect(() => {
		if (!isLoading) {
			setHasLoadedOnce(true);
		}
	}, [isLoading]);
	const isInitialLoading = isLoading && !hasLoadedOnce;

	const refreshAuth = useCallback(async () => {
		await auth.refreshAuth();
		const result = await refetchParker();
		if (result.error) {
			throw result.error;
		}
		if (!result.data) {
			await utils.invalidate();
			throw new Error("No parker data returned");
		}
		return result.data;
	}, [auth, refetchParker, utils]);

	const value = useMemo((): ParkerContextValue => {
		if (auth.user && parkerAccountsEnabled) {
			return {
				parker: parker || {
					firstName: auth.user.firstName ?? "",
					lastName: auth.user.lastName ?? "",
					email: auth.user.email ?? "",
					defaultVehicle: null,
				},
				displayName: [auth.user.firstName, auth.user.lastName].filter(Boolean).join(" ") || auth.user.email,
				error: error ?? undefined,
				isLoading,
				isInitialLoading,
				isLoggedIn: true,
				needsParkerCreation: !parker,
				isConnectedParker: !!auth.user.externalId,
				enabled: true,
				refreshAuth,
			};
		}
		return {
			parker: undefined,
			displayName: undefined,
			error: error ?? undefined,
			isLoading,
			isInitialLoading,
			isLoggedIn: false,
			isConnectedParker: false,
			needsParkerCreation: false,
			enabled: parkerAccountsEnabled,
			refreshAuth,
		};
	}, [auth, parker, error, isLoading, isInitialLoading, parkerAccountsEnabled, refreshAuth]);

	return <ParkerStateProvider value={value}>{children}</ParkerStateProvider>;
};
